import { useState } from 'react';
import { T } from '../lib/design-tokens';

interface ContactFormProps {
  onSubmit: (contact: { name: string; phone: string; email?: string }) => void;
  channel: 'voice' | 'email';
  initialValues?: { name?: string; phone?: string; email?: string };
}

const E164_RE = /^\+[1-9]\d{1,14}$/;

export function ContactForm({ onSubmit, channel, initialValues }: ContactFormProps) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [phone, setPhone] = useState(initialValues?.phone ?? '');
  const [email, setEmail] = useState(initialValues?.email ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim() || name.trim().length > 200) errs.name = 'Name is required (1-200 chars)';
    if (channel === 'voice') {
      if (!phone.trim()) errs.phone = 'Phone number is required for voice tasks';
      else if (!E164_RE.test(phone.trim())) errs.phone = 'Must be E.164 format (e.g. +14155551234)';
    }
    if (channel === 'email') {
      if (!email.trim()) errs.email = 'Email is required for email tasks';
      else if (!email.includes('@')) errs.email = 'Invalid email address';
    }
    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    onSubmit({ name: name.trim(), phone: phone.trim(), email: email.trim() || undefined });
  };

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: hasError ? `1.5px solid ${T.red}` : `1.5px solid ${T.glassBorder}`,
    fontFamily: T.sans,
    fontSize: 14,
    color: T.cream,
    outline: 'none',
    background: T.surface,
    transition: 'border-color 0.2s',
  });

  return (
    <div
      className="rounded-[14px] flex flex-col gap-4"
      style={{
        padding: '24px 28px',
        border: `1.5px solid ${T.goldDim}`,
        background: T.goldGlow,
      }}
    >
      <div
        className="text-xs font-semibold uppercase"
        style={{ color: T.gold, letterSpacing: '0.08em', fontFamily: T.sans }}
      >
        Contact Information Required
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: T.cream60, fontFamily: T.sans }}>
          Name *
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Contact name"
          style={inputStyle(!!errors.name)}
          onFocus={(e) => { e.currentTarget.style.borderColor = T.gold; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = errors.name ? T.red : T.glassBorder; }}
        />
        {errors.name && (
          <div className="text-xs mt-1" style={{ color: T.red, fontFamily: T.sans }}>{errors.name}</div>
        )}
      </div>

      {channel === 'voice' && (
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: T.cream60, fontFamily: T.sans }}>
            Phone *
          </label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14155551234"
            style={inputStyle(!!errors.phone)}
            onFocus={(e) => { e.currentTarget.style.borderColor = T.gold; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = errors.phone ? T.red : T.glassBorder; }}
          />
          {errors.phone && (
            <div className="text-xs mt-1" style={{ color: T.red, fontFamily: T.sans }}>{errors.phone}</div>
          )}
        </div>
      )}

      {channel === 'email' && (
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: T.cream60, fontFamily: T.sans }}>
            Email *
          </label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="contact@example.com"
            type="email"
            style={inputStyle(!!errors.email)}
            onFocus={(e) => { e.currentTarget.style.borderColor = T.gold; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = errors.email ? T.red : T.glassBorder; }}
          />
          {errors.email && (
            <div className="text-xs mt-1" style={{ color: T.red, fontFamily: T.sans }}>{errors.email}</div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        className="self-start border-none cursor-pointer"
        style={{
          padding: '8px 18px',
          borderRadius: 8,
          background: T.gold,
          color: T.bg,
          fontSize: 13,
          fontWeight: 600,
          fontFamily: T.sans,
        }}
      >
        Save Contact
      </button>
    </div>
  );
}
